import React from 'react';
import { observer } from 'mobx-react-lite';
import { AppointmentSlotDuration } from '@uplift/api';
import { AppointmentDurationMappingDurationMinutes, CalendarEventPopover, CalendarEventPopoverProps, AddSessionFormFormik } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';
import { useCalendarStore } from '@uplift/contexts';
import { CalendarViewTypes } from '@uplift/types';
import { Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';


export type AddSessionPopoverProps = Omit<CalendarEventPopoverProps, 'placement'> & {
  setCurrentEvent: (e: { startStr: string; endStr: string }) => void;
};


export const AddSessionPopover: React.FC<AddSessionPopoverProps> = observer(({ setCurrentEvent, open, ...props }) => {
  const queryClient = useQueryClient();
  const calendarStore = useCalendarStore();

  const handleDateChange = React.useCallback((_date: string) => {
    calendarStore.calendarApi?.gotoDate(_date);
  }, [ calendarStore.calendarApi ]);

  const handleEventParamsChange = React.useCallback((startTime: string, duration: AppointmentSlotDuration) => {
    if (!open) return;

    const durationMinutes = AppointmentDurationMappingDurationMinutes[duration];

    setCurrentEvent({
      startStr: startTime,
      endStr: CalendarDateService.date(startTime).add(durationMinutes, 'minutes').format(),
    });
  }, [ open, setCurrentEvent ]);

  return (
    <CalendarEventPopover
      open={open}
      onClose={props.onClose}
      anchor={props.anchor}
      autoScroll={props.autoScroll}
      placement={calendarStore.calendarApi?.view.type === CalendarViewTypes.timeGridDay ? 'bottom' : undefined}
    >
      <Box width="500px">
        <AddSessionFormFormik
          open={open}
          onClose={props.onClose}
          onSuccess={async () => {
            await queryClient.invalidateQueries([ 'icsSubscription' ]);
            await calendarStore.loadAllEvents(calendarStore._lastInfo, true, false);
            props.onClose();
          }}
          onDateChange={handleDateChange}
          onEventParamsChange={handleEventParamsChange}
          newEvent={calendarStore.newEvent.time}
          calendarApi={calendarStore.calendarApi}
        />
      </Box>
    </CalendarEventPopover>
  );
});
